import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import {
  Chart,
  ChartData,
  ChartDataset,
  ChartOptions,
  LegendItem,
  Plugin,
  TooltipModel,
} from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { TimeSeriesCardConfig, TimeSeriesCardDataset } from '../card.component';
import { PrettyNumberPipeHelperService } from '../../../../services/pretty-number-pipe-helper/pretty-number-pipe-helper.service';
import {
  ChartTooltipContent,
  ChartTooltipManager,
  ChartTooltipService,
} from '../../../../services/chart-tooltip/chart-tooltip.service';
import { MatIconModule } from '@angular/material/icon';
import { ChartThemeService } from '../../../../services/chart-theme/chart-theme.service';

@Component({
  selector: 'app-time-series-card-content',
  imports: [CommonModule, BaseChartDirective, MatIconModule],
  templateUrl: './time-series-card-content.component.html',
  styleUrl: './time-series-card-content.component.scss',
})
export class TimeSeriesCardContentComponent implements OnChanges, OnDestroy {
  @Input() config!: TimeSeriesCardConfig;

  chartType: 'bar' | 'line' = 'bar';
  chartData: ChartData<'bar' | 'line', number[], string> = {
    labels: [],
    datasets: [],
  };
  chartOptions: ChartOptions<'bar' | 'line'> = {};
  chartPlugins: Plugin<'bar' | 'line'>[] = [];

  private readonly _tooltipManager: ChartTooltipManager<'bar'>;

  constructor(
    private readonly _prettyNumber: PrettyNumberPipeHelperService,
    tooltipService: ChartTooltipService,
    private readonly _theme: ChartThemeService,
  ) {
    this._tooltipManager = tooltipService.createManager<'bar'>({
      transform: 'translate(-50%, calc(-100% - 12px))',
      render: ({ tooltip }) => this._renderTooltip(tooltip),
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']) {
      this._rebuildChart();
    }
  }

  ngOnDestroy(): void {
    this._tooltipManager.destroy();
  }

  private _rebuildChart(): void {
    this._tooltipManager.hide();

    if (!this.config || !this.config.labels?.length) {
      this.chartData = { labels: [], datasets: [] };
      return;
    }

    const chartType: 'bar' | 'line' =
      this.config.chartStyle === 'line' ? 'line' : 'bar';
    const colors = this._theme.getSeriesColors(this.config.colorIndex ?? 0, {
      seriesCount: this.config.datasets.length,
      chartType: chartType,
    });

    const datasets = this._toChartDatasets(
      this.config.datasets,
      colors,
      chartType,
    );

    this.chartType = chartType;
    this.chartData = {
      labels: [...this.config.labels],
      datasets,
    };

    const unit = this.config.unit;
    this.chartOptions = this._buildChartOptions(unit);
  }

  private _toChartDatasets(
    datasets: TimeSeriesCardDataset[],
    colors: string[],
    chartType: 'bar' | 'line',
  ): ChartDataset<'bar' | 'line', number[]>[] {
    const result: ChartDataset<'bar' | 'line', number[]>[] = [];
    const totalDatasets = datasets.length;

    datasets.forEach((dataset, index) => {
      const color = colors[index % colors.length] ?? colors[0] ?? '#2ed9c2';
      const values = dataset.values.map((value) => {
        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : 0;
      });

      result.push({
        type: chartType,
        label: dataset.label,
        data: values,
        backgroundColor: color,
        borderColor: color,
        borderRadius: chartType === 'bar' ? 15 : undefined,
        borderSkipped: false,
        tension: chartType === 'line' ? 0.25 : undefined,
        pointRadius: chartType === 'line' ? 3 : 0,
        pointBackgroundColor: color,
        pointBorderColor: color,
        order: dataset.variant === 'primary' ? totalDatasets - index : index,
      });
    });

    return result;
  }

  private _buildChartOptions(unit?: string): ChartOptions<'bar' | 'line'> {
    const prettyHelper = this._prettyNumber;

    return {
      maintainAspectRatio: false,
      animation: {
        duration: 500,
        easing: 'easeOutCubic',
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            maxRotation: 0,
            minRotation: 0,
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            display: false,
          },
          ticks: {
            callback: (value) =>
              prettyHelper.transform(Number(value), true, unit),
          },
        },
      },
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
          align: 'start',
          labels: {
            usePointStyle: true, // enables circular legend markers
            pointStyle: 'circle',
            font: {
              family: 'Poppins',
              size: 14,
            },
          },
        },
        tooltip: {
          enabled: false,
          external: (context) => this._tooltipManager.handler(context as any),
        },
      },
    };
  }

  private _renderTooltip(
    tooltip: TooltipModel<'bar'>,
  ): ChartTooltipContent | null {
    if (!tooltip || !tooltip.dataPoints?.length || !this.config) {
      return null;
    }

    const dataPoint = tooltip.dataPoints[0];
    const rawValue = this._extractTooltipValue(dataPoint);
    const formattedValue = this._prettyNumber.transform(
      rawValue,
      false,
      this.config.unit,
    );

    const title = tooltip.title?.[0] ?? dataPoint.label ?? this.config.title;
    const datasetLabel = dataPoint.dataset?.label ?? this.config.title;
    const indicatorColor =
      tooltip.labelColors?.[0]?.borderColor?.toString() ??
      tooltip.labelColors?.[0]?.backgroundColor?.toString() ??
      undefined;

    return {
      title,
      lines: [
        {
          label: datasetLabel,
          value: formattedValue,
          indicatorColor,
        },
      ],
    };
  }

  private _extractTooltipValue(
    dataPoint: TooltipModel<'bar'>['dataPoints'][number],
  ): number {
    if (!dataPoint) {
      return 0;
    }

    if (typeof dataPoint.parsed === 'number') {
      return this._sanitizeNumber(dataPoint.parsed);
    }

    const parsed = dataPoint.parsed as unknown;
    if (parsed && typeof parsed === 'object' && 'y' in (parsed as any)) {
      const value = (parsed as Record<string, unknown>)['y'];
      return this._sanitizeNumber(value);
    }

    if (typeof dataPoint.raw === 'number') {
      return this._sanitizeNumber(dataPoint.raw);
    }

    return this._sanitizeNumber(Number(dataPoint.raw));
  }

  private _sanitizeNumber(value: unknown): number {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
  }
}
